import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { AlertCircle, RefreshCw } from "lucide-react";

const CANCELLATION_REASONS = [
  { id: "high_price", label: "Driver Demanding High Price" },
  { id: "driver_delay", label: "Driver Delay" },
  { id: "not_reachable", label: "Driver Not Reachable" },
  { id: "driver_asked", label: "Driver Asked to Cancel the Ride" },
  { id: "other", label: "Other" },
];

export const CancelRideDialog = ({
  open,
  onOpenChange,
  cancelReason,
  setCancelReason,
  customCancelReason,
  setCustomCancelReason,
  reassignToOther,
  setReassignToOther,
  cancelling,
  onCancel,
  hasDriver,
}) => {
  const isDisabled = !cancelReason || (cancelReason === "other" && !customCancelReason) || cancelling;

  const handleCancelAndReassign = () => {
    setReassignToOther(true);
    // Pass true directly since state update is async
    onCancel(true);
  };

  const handleJustCancel = () => {
    setReassignToOther(false);
    // Pass false directly since state update is async
    onCancel(false);
  };

  const handleClose = () => {
    onOpenChange(false);
    setCancelReason("");
    setCustomCancelReason("");
    setReassignToOther(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md w-[95vw]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-destructive" />
            Cancel Ride
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <p className="text-sm text-muted-foreground">
            Please select a reason for cancellation:
          </p>

          <div className="space-y-2">
            {CANCELLATION_REASONS.map((reason) => (
              <button
                key={reason.id}
                onClick={() => setCancelReason(reason.id)}
                className={`w-full p-3 rounded-lg border text-left transition-all ${
                  cancelReason === reason.id
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <span className="text-sm font-medium">{reason.label}</span>
              </button>
            ))}
          </div>

          {cancelReason === "other" && (
            <Textarea
              placeholder="Please describe your reason..."
              value={customCancelReason}
              onChange={(e) => setCustomCancelReason(e.target.value)}
              className="resize-none"
              rows={3}
            />
          )}
        </div>

        <div className="flex flex-col gap-3">
          {/* Cancel and Find Another Driver Button */}
          {hasDriver && (
            <Button
              variant="default"
              className="w-full bg-primary hover:bg-primary/90"
              onClick={handleCancelAndReassign}
              disabled={isDisabled}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {cancelling && reassignToOther ? "Finding another driver..." : "Cancel & Find Another Driver"}
            </Button>
          )}

          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={handleClose}>
              Go Back
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={handleJustCancel}
              disabled={isDisabled}
            >
              {cancelling && !reassignToOther ? "Cancelling..." : "Just Cancel"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CancelRideDialog;
